import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { useCart } from '../../../hooks/useCart'
import {
  addToWishlist,
  addToWishlistLocal,
  removeFromWishlist,
  removeFromWishlistLocal,
  selectWishlistItems,
} from '../../../store/slices/wishlistSlice'
import { AppDispatch } from '../../../store/store'
import { Product, ProductPriceResponse } from '../../../types/product'
import {
  buildProductCartMeta,
  getDiscountPercent,
  getEffectivePrice,
  getProductDisplayWeight,
  getSortedVariants,
  getVariantLabel,
  hasCatalogDiscount,
  isCapsuleProduct,
  isDripProduct,
} from '../../../utils/cartUtils'
import CustomSelect from '../CustomSelect/CustomSelect'
import { DiscountBadge } from '../ProductMarkers/ProductMarkers'
import styles from './ProductInfo.module.css'

interface ProductInfoProps {
  product: Product
}

export default function ProductInfo({ product }: ProductInfoProps) {
  const dispatch = useDispatch<AppDispatch>()
  const { addItem } = useCart()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [quantity, setQuantity] = useState(1)
  const wishlistItems = useSelector(selectWishlistItems)
  const isInWishlist = useMemo(
    () => wishlistItems.some(item => item.id === product.id),
    [wishlistItems, product.id],
  )
  const [grindType, setGrindType] = useState('grain')
  const [openAccordion, setOpenAccordion] = useState<string | null>(null)
  const [isInlineCartVisible, setIsInlineCartVisible] = useState(false)
  const actionsRef = useRef<HTMLDivElement>(null)

  const sortedVariants = getSortedVariants(product.prices || [])
  const [selectedVariant, setSelectedVariant] = useState<ProductPriceResponse | null>(
    sortedVariants[0] ?? null,
  )
  const hasVariants = sortedVariants.length > 0
  const effectivePrice = selectedVariant ? getEffectivePrice(selectedVariant) : null

  // Grinding options for package coffee
  const grindingOptions = [
    { value: 'grain', label: 'У зерні' },
    { value: 'jezva', label: 'Джезва' },
    { value: 'geyser', label: 'Гейзерна кавоварка' },
    { value: 'espresso', label: 'Еспресо' },
    { value: 'cup', label: 'Чашка' },
    { value: 'aeropress', label: 'Аеропрес' },
    { value: 'v60', label: 'V60' },
    { value: 'chemex', label: 'Кемекс' },
    { value: 'filter', label: 'Фільтр' },
    { value: 'french-press', label: 'Френч-прес' },
  ]

  const isCapsule = isCapsuleProduct(product)
  const isDrip = isDripProduct(product) && !isCapsule
  const isPackageCoffee = !isCapsule && !isDrip

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, quantity + delta)
    setQuantity(newQuantity)
  }

  const handleWishlistToggle = async () => {
    try {
      if (isAuthenticated) {
        // For authenticated users, use API calls
        if (isInWishlist) {
          const result = await dispatch(removeFromWishlist(product.id) as any)
          if (removeFromWishlist.rejected.match(result)) {
            // Failed to remove from wishlist
          }
        } else {
          const result = await dispatch(addToWishlist({ productId: product.id, product }) as any)
          if (addToWishlist.rejected.match(result)) {
            // Failed to add to wishlist
          }
        }
      } else {
        // For unauthorized users, use local storage
        if (isInWishlist) {
          dispatch(removeFromWishlistLocal(product.id))
        } else {
          dispatch(addToWishlistLocal(product))
        }
      }
    } catch (error) {
      // Wishlist toggle error
    }
  }

  const buildCartItem = () => {
    const selectedGrindOption = grindingOptions.find(option => option.value === grindType)
    return {
      id: parseInt(product.id),
      name: product.name,
      price: effectivePrice ?? 0,
      regular_price: selectedVariant?.price ?? 0,
      sale_price: hasCatalogDiscount(selectedVariant?.discount)
        ? (effectivePrice ?? undefined)
        : undefined,
      discount_percent: getDiscountPercent(selectedVariant?.discount),
      image: product.mainPicture || '/images/product.png',
      sku: product.id,
      quantity: quantity,
      chosenVariantId: selectedVariant?.id,
      variants: sortedVariants,
      meta_data: buildProductCartMeta(product, {
        grindLabel: selectedGrindOption?.label || 'У зерні',
        variant: selectedVariant,
      }),
    }
  }

  const handleAddToCart = () => {
    addItem(buildCartItem())
  }

  const handleQuickOrder = () => {
    addItem(buildCartItem())
    navigate('/checkout')
  }

  const handleAccordionToggle = (accordionId: string) => {
    setOpenAccordion(openAccordion === accordionId ? null : accordionId)
  }

  const getPrice = () => {
    if (effectivePrice !== null) return `${effectivePrice} ₴`
    return 'Ціна за запитом'
  }

  const getWeight = () => getProductDisplayWeight(product, selectedVariant)

  const renderPriceContent = () => (
    <>
      {selectedVariant && hasCatalogDiscount(selectedVariant.discount) ? (
        <>
          <span className={styles.priceValue}>{effectivePrice} ₴</span>
          <span className={styles.originalPrice}>{selectedVariant.price} ₴</span>
          <DiscountBadge discount={selectedVariant.discount} />
        </>
      ) : (
        <span className={styles.priceValue}>{getPrice()}</span>
      )}
      <span className={styles.priceWeight}>/ {getWeight()}</span>
    </>
  )

  useEffect(() => {
    const node = actionsRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => setIsInlineCartVisible(entry.isIntersecting),
      { threshold: 0.75, rootMargin: '0px 0px -20% 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const renderRating = (value: number | null, max: number = 10) => {
    if (value === null) return null
    return (
      <div className={styles.rating}>
        <span className={styles.ratingValue}>
          {value}/{max}
        </span>
      </div>
    )
  }

  return (
    <div className={styles.productInfo}>
      {/* Category */}
      <div className={styles.category}>{product.beanType?.label || 'Тип зерна не вказано'}</div>

      {/* Title and Wishlist */}
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{product.name}</h1>
        <button
          className={`${styles.wishlistBtn} ${isInWishlist ? styles.active : ''}`}
          onClick={handleWishlistToggle}
          aria-label={isInWishlist ? 'Прибрати з бажаного' : 'Додати до бажаного'}
          aria-pressed={isInWishlist}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
          >
            <path
              d="M6.10523 22.0226L17.8244 34.3733C19.0074 35.6201 20.9939 35.6201 22.1769 34.3733L33.8961 22.0226C37.5911 18.1285 37.5911 11.8149 33.8961 7.92076C30.2011 4.02664 24.2103 4.02664 20.5153 7.92076V7.92076C20.2355 8.21559 19.7658 8.21559 19.486 7.92076V7.92076C15.791 4.02664 9.80023 4.02664 6.10523 7.92076C2.41023 11.8149 2.41024 18.1285 6.10523 22.0226Z"
              stroke="#BFBFBF"
              strokeWidth="1.5"
            />
          </svg>
        </button>
      </div>

      {/* Description */}
      <p className={styles.description}>
        {product.description ||
          `${product.originCountry} ${product.originRegion} - ${
            product.beanType?.label || 'кава'
          } з ${product.processingMethod?.label || 'спеціальною обробкою'}. ${
            product.scaScore ? `SCA бал: ${product.scaScore}/100.` : ''
          }`}
      </p>

      <div className={styles.mainInfo}>
        {/* Specifications */}
        <div className={styles.specifications}>
          {product.beanType?.label && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Тип зерна</span>
              <span className={styles.specValue}>{product.beanType.label}</span>
            </div>
          )}

          {product.beanVariety?.label && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Різновид зерна</span>
              <span className={styles.specValue}>{product.beanVariety.label}</span>
            </div>
          )}

          {product.sweetness && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Солодкість</span>
              {renderRating(product.sweetness)}
            </div>
          )}

          {product.bitterness && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Гіркота</span>
              {renderRating(product.bitterness)}
            </div>
          )}

          {product.acidity && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Кислотність</span>
              {renderRating(product.acidity)}
            </div>
          )}

          {product.originCountry && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Походження</span>
              <span className={styles.specValue}>{product.originCountry}</span>
            </div>
          )}

          {product.originRegion && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Регіон</span>
              <span className={styles.specValue}>{product.originRegion}</span>
            </div>
          )}

          {product.scaScore && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>SCA бал</span>
              <span className={styles.specValue}>{product.scaScore}/100</span>
            </div>
          )}

          {product.processingMethod?.label && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Метод обробки</span>
              <span className={styles.specValue}>{product.processingMethod.label}</span>
            </div>
          )}

          {product.roastType?.label && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Вид обсмажки</span>
              <span className={styles.specValue}>{product.roastType.label}</span>
            </div>
          )}

          {product.packageProductType?.label && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Тип продукту</span>
              <span className={styles.specValue}>{product.packageProductType.label}</span>
            </div>
          )}

          {product.farm && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Ферма</span>
              <span className={styles.specValue}>{product.farm}</span>
            </div>
          )}

          {product.altitudeMeters && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Висота зростання</span>
              <span className={styles.specValue}>{product.altitudeMeters}м</span>
            </div>
          )}

          {product.harvestYear && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Рік збору</span>
              <span className={styles.specValue}>{product.harvestYear}</span>
            </div>
          )}

          {product.roastDate && (
            <div className={styles.specItem}>
              <span className={styles.specLabel}>Дата обсмажки</span>
              <span className={styles.specValue}>{product.roastDate}</span>
            </div>
          )}
        </div>

        {/* Product Options */}
        <div className={styles.options}>
          {/* Weight / variant pills */}
          {sortedVariants.length > 1 && (
            <div className={styles.option}>
              <label className={styles.optionLabel}>Фасування:</label>
              <div className={styles.variantPills}>
                {sortedVariants.map(variant => (
                  <button
                    key={variant.id}
                    className={`${styles.variantPill} ${selectedVariant?.id === variant.id ? styles.variantPillActive : ''}`}
                    onClick={() => setSelectedVariant(variant)}
                  >
                    {getVariantLabel(product, variant)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {sortedVariants.length === 1 && selectedVariant && (
            <div className={styles.option}>
              <label className={styles.optionLabel}>Фасування:</label>
              <span className={styles.variantSingle}>{getVariantLabel(product, selectedVariant)}</span>
            </div>
          )}

          {/* Grinding selection - only for package coffee */}
          {isPackageCoffee && (
            <div className={styles.option}>
              <label className={styles.optionLabel}>Як змолоти:</label>
              <CustomSelect
                options={grindingOptions}
                value={grindType}
                onChange={setGrindType}
              />
            </div>
          )}

          {/* Quantity */}
          <div className={styles.quantity}>
            <label className={styles.quantityLabel}>Кількість:</label>
            <div className={styles.quantityControls}>
              <button
                type="button"
                className={styles.quantityBtn}
                onClick={() => handleQuantityChange(-1)}
                aria-label="Зменшити кількість"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="2"
                  viewBox="0 0 10 2"
                  fill="none"
                >
                  <path
                    d="M1.25 1L8.75 1"
                    stroke="#0D0D0D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <input
                type="number"
                value={quantity}
                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className={styles.quantityInput}
                min="1"
              />
              <button
                type="button"
                className={styles.quantityBtn}
                onClick={() => handleQuantityChange(1)}
                aria-label="Збільшити кількість"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                >
                  <path
                    d="M1.25 5L8.75 5"
                    stroke="#0D0D0D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5 1.24994L5 8.74994"
                    stroke="#0D0D0D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          {/* Action Buttons */}
          <div className={styles.actions} ref={actionsRef}>
            {/* Price */}
            <div className={styles.price}>{renderPriceContent()}</div>

            <button
              className={styles.addToCartBtn}
              onClick={handleAddToCart}
              disabled={!hasVariants}
            >
              <CartIcon />
              <span>В кошик</span>
            </button>
            <button className={styles.quickOrderBtn} onClick={handleQuickOrder}>
              Швидке замовлення
            </button>
          </div>
        </div>
      </div>

      {/* Accordion Sections */}
      <div className={styles.accordion}>
        <div className={styles.accordionItem}>
          <button
            className={`${styles.accordionHeader} ${
              openAccordion === 'brewing' ? styles.active : ''
            }`}
            onClick={() => handleAccordionToggle('brewing')}
          >
            Рекомендації по заварці
            <span
              className={`${styles.accordionIcon} ${
                openAccordion === 'brewing' ? styles.rotated : ''
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="12"
                viewBox="0 0 22 12"
                fill="none"
              >
                <path
                  d="M21.5 1.00037L21.0156 1.00037C15.4823 1.00037 11.0008 5.49388 11.0156 11.0272L11.0169 11.5"
                  stroke="#0D0D0D"
                  strokeWidth="1.5"
                />
                <path
                  d="M0.5 1L0.984388 1C6.51771 1 10.9992 5.49351 10.9844 11.0268L10.9831 11.4996"
                  stroke="#0D0D0D"
                  strokeWidth="1.5"
                />
              </svg>
            </span>
          </button>
          <div
            className={`${styles.accordionContent} ${
              openAccordion === 'brewing' ? styles.open : ''
            }`}
          >
            <div className={styles.content}>
              {isCapsule ? (
                <>
                  <p>Рецепт для капсульної кави:</p>
                  <ul>
                    <li>Вставте капсулу у кавоварку</li>
                    <li>Оберіть об'єм напою (25 мл для еспресо, 40 мл для лунго)</li>
                    <li>Натисніть кнопку приготування</li>
                    <li>Насолоджуйтесь напоєм</li>
                  </ul>
                </>
              ) : isPackageCoffee ? (
                <>
                  <p>Наш рецепт для V-60:</p>
                  <ul>
                    <li>Помел - ми допоможемо змолоти як треба (якщо сам, то щось схоже на морську сіль)</li>
                    <li>Кава - 18 г</li>
                    <li>Вода - 300 мл, температура - 93 градуси</li>
                  </ul>
                  <p></p>
                  <ul>
                    <li>50 г - 30 секунд на цвітіння та дегазацію</li>
                    <li>0:30 - 150 г води</li>
                    <li>1:30 - 100 г води</li>
                    <li>Загальний час: 2:30-2:40</li>
                    <li>Чекаємо повного проливу води. Переливаємо у чашку і насолоджуємося</li>
                  </ul>
                </>
              ) : (
                <>
                  <p>Наш рецепт для дріпа:</p>
                  <ul>
                    <li>Відкриваємо верхню смужку на дріпі та ставимо дріп на чашку</li>
                  </ul>
                    <p></p>
                  <ul>
                    <li>Заливаємо 60 мл води температурою 92-93 градуса</li>
                    <li>Чекаємо 30 секунд</li>
                    <li>Повторно заливаємо 60 мл води ще двічі після кожного проливу</li>
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        <div className={styles.accordionItem}>
          <button
            className={`${styles.accordionHeader} ${
              openAccordion === 'delivery' ? styles.active : ''
            }`}
            onClick={() => handleAccordionToggle('delivery')}
          >
            Доставка/оплата
            <span
              className={`${styles.accordionIcon} ${
                openAccordion === 'delivery' ? styles.rotated : ''
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="12"
                viewBox="0 0 22 12"
                fill="none"
              >
                <path
                  d="M21.5 1.00037L21.0156 1.00037C15.4823 1.00037 11.0008 5.49388 11.0156 11.0272L11.0169 11.5"
                  stroke="#0D0D0D"
                  strokeWidth="1.5"
                />
                <path
                  d="M0.5 1L0.984388 1C6.51771 1 10.9992 5.49351 10.9844 11.0268L10.9831 11.4996"
                  stroke="#0D0D0D"
                  strokeWidth="1.5"
                />
              </svg>
            </span>
          </button>
          <div
            className={`${styles.accordionContent} ${
              openAccordion === 'delivery' ? styles.open : ''
            }`}
          >
            <div className={styles.content}>
              <p>Доставка</p>
              <p>
                Замовлення доставляються по всій Україні службою доставки Нова Пошта або кур’єром Нова Пошта.
                <br />
                Замовлення оплачені у робочі дні до 15:00 ми відправляємо в той самий день, оплачені після 15:00 – наступного робочого дня. Оплачені у вихідні та святкові дні – наступного робочого дня.
                <br />
                Термін доставки 1-3 дні після відправки. Вартість, розраховується за тарифами Нова Пошта.
                <br />
                Самовивіз за адресою: м.Київ, Велика Васильківська 62а.
              </p>
            </div>
            <div className={styles.content}>
              <p>Оплата</p>
              <p>
                Мінімальне замовлення не обмежене.
                <br />
                Оплата онлайн за допомогою платіжного сервісу Mono pay.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.accordionItem}>
          <button
            className={`${styles.accordionHeader} ${
              openAccordion === 'return' ? styles.active : ''
            }`}
            onClick={() => handleAccordionToggle('return')}
          >
            Обмін та повернення
            <span
              className={`${styles.accordionIcon} ${
                openAccordion === 'return' ? styles.rotated : ''
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="12"
                viewBox="0 0 22 12"
                fill="none"
              >
                <path
                  d="M21.5 1.00037L21.0156 1.00037C15.4823 1.00037 11.0008 5.49388 11.0156 11.0272L11.0169 11.5"
                  stroke="#0D0D0D"
                  strokeWidth="1.5"
                />
                <path
                  d="M0.5 1L0.984388 1C6.51771 1 10.9992 5.49351 10.9844 11.0268L10.9831 11.4996"
                  stroke="#0D0D0D"
                  strokeWidth="1.5"
                />
              </svg>
            </span>
          </button>
          <div
            className={`${styles.accordionContent} ${
              openAccordion === 'return' ? styles.open : ''
            }`}
          >
            <div className={styles.content}>
              <p>Повернення</p>
              <p>
                Ми здійснюємо обмін та повернення товарів відповідно до Закону України «Про захист прав споживачів».
                Перелік товарів, що підлягають обміну та поверненню, визначено Постановою Кабінету Міністрів України від 19 березня 1994 р. №172.
              </p>
            </div>
            <div className={styles.content}>
              <p>Обмін</p>
              <p>
                Необхідні умови повернення товару: <br />
                Товар не був у експлуатації; <br />
                Упаковка товару не порушена; <br />
                Збережений товарний чек і гарантійний талон, якщо він передбачений; <br />
                Замовлення з меленою кавою поверненню і обміну не підлягають. <br />
                Ми завжди йдемо назустріч нашим клієнтам. Якщо Ви залишилися незадоволені якістю
                придбаного товару, ми готові в індивідуальному порядку розглянути Ваш випадок і
                вирішити його.
                <br />
                Пошта для запитань і зауважень: contact@seaters.coffee
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`${styles.stickyCartBar} ${isInlineCartVisible ? styles.stickyCartBarHidden : ''}`}
      >
        <div className={styles.stickyPrice}>{renderPriceContent()}</div>
        <button
          type="button"
          className={styles.stickyAddToCartBtn}
          onClick={handleAddToCart}
          disabled={!hasVariants}
        >
          <CartIcon />
          <span>В кошик</span>
        </button>
      </div>
    </div>
  )
}

function CartIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
      <path d="M12.5007 9.99998H5.16732L4.16732 3.99998H14.5007L12.5007 9.99998Z" fill="white" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.83398 1.99998C1.83398 1.63179 2.13246 1.33331 2.50065 1.33331H3.83398C4.15988 1.33331 4.438 1.56892 4.49158 1.89038L4.73207 3.33331H14.5007C14.7149 3.33331 14.9162 3.43633 15.0415 3.61017C15.1668 3.78402 15.2009 4.0075 15.1331 4.2108L13.1331 10.2108C13.0424 10.483 12.7876 10.6666 12.5007 10.6666H5.16732C4.84142 10.6666 4.5633 10.431 4.50972 10.1096L3.26923 2.66665H2.50065C2.13246 2.66665 1.83398 2.36817 1.83398 1.99998ZM4.95429 4.66665L5.73207 9.33331H12.0201L13.5757 4.66665H4.95429Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.50065 13.3333C4.50065 12.5969 5.0976 12 5.83398 12C6.57036 12 7.16732 12.5969 7.16732 13.3333C7.16732 14.0697 6.57036 14.6666 5.83398 14.6666C5.0976 14.6666 4.50065 14.0697 4.50065 13.3333Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.5007 13.3333C10.5007 12.5969 11.0976 12 11.834 12C12.5704 12 13.1673 12.5969 13.1673 13.3333C13.1673 14.0697 12.5704 14.6666 11.834 14.6666C11.0976 14.6666 10.5007 14.0697 10.5007 13.3333Z"
        fill="white"
      />
    </svg>
  )
}
